import {
  defaultLoadoutSlots,
  homeRulesDefaults,
  inventoryRulesBlockDefaults,
  itemGroupDefaults,
  loadoutRulesDefaults,
} from '../../../lib/hb/defaults';
import type {
  HomeRulesDef,
  InventoryRulesDef,
  ItemGroupDef,
  ItemMatchDef,
  LoadoutRulesDef,
  LoadoutSlotDef,
} from '../../../lib/hb/types';
import { issuesUnder } from '../../../lib/validate';
import { withDefaults } from '../../../lib/teamsDefaults';
import {
  CheckboxField,
  Fieldset,
  IssueList,
  ItemIdListEditor,
  ListEditor,
  NumberField,
  StringListEditor,
  TextField,
} from '../fields';
import { useContentBundle } from '../refs';
import type { FormProps } from '../types';
import { TierSelectField } from '../wind/WindForms';

type NumOpts = { integer?: boolean; min?: number; max?: number; step?: number };

/**
 * `homeRules` (shared-cs `HomeRulesDef`, ADR 0046, PLAN §9 and §14): infirmary admission, home training marks, the headline, the
 * supply thresholds, and the on-the-table and log sizes. Absent fields show the C# defaults.
 */
export function HomeRulesForm({ value, onChange, errors }: FormProps<HomeRulesDef>) {
  const d = homeRulesDefaults();
  const h = withDefaults(d, value ?? {});
  const beds = { ...d.beds, ...h.beds };
  const headline = { ...d.headline, ...h.headline };
  const supplies = { ...d.supplies, ...h.supplies };
  const set = (patch: Partial<HomeRulesDef>) => onChange({ ...value, ...patch });
  const field = (
    path: string,
    label: string,
    hint: string,
    v: number | undefined,
    write: (n: number) => void,
    o: NumOpts = {},
  ) => (
    <NumberField
      key={path}
      label={label}
      hint={hint}
      integer={o.integer}
      min={o.min ?? 0}
      max={o.max}
      step={o.step}
      value={v}
      issues={errors[path]}
      onChange={(n) => write(n ?? 0)}
    />
  );
  const top = (k: keyof HomeRulesDef & string, label: string, hint: string, o: NumOpts = {}) =>
    field(k, label, hint, h[k] as number | undefined, (n) => set({ [k]: n }), o);
  const share = { max: 1, step: 0.05 };
  return (
    <>
      <Fieldset legend="Infirmary beds">
        {field(
          'beds.admitBelowHealthFrac',
          'Admit below health share',
          'A hurt survivor below this share of max HP takes a free bed. (0, 1].',
          beds.admitBelowHealthFrac,
          (n) => set({ beds: { ...beds, admitBelowHealthFrac: n } }),
          share,
        )}
        {field(
          'beds.minDurationMult',
          'Shortest stay multiplier',
          'Bed and skill cuts never take recovery below this share. (0, 1].',
          beds.minDurationMult,
          (n) => set({ beds: { ...beds, minDurationMult: n } }),
          share,
        )}
      </Fieldset>
      <Fieldset legend="Home training (WITS builds, CARE beds)">
        {top(
          'buildMarkMinSeconds',
          'Build mark min seconds',
          'A build counts toward a WITS mark when its steps take at least this long.',
          { integer: true },
        )}
        {top(
          'dischargeMarkMinHours',
          'Discharge mark min hours',
          'A bed stay this long earns the carer a CARE mark…',
        )}
        {top(
          'dischargeMarkMinRest',
          'Discharge mark min rest (winds)',
          '…or one that cleared at least this much rest debt (WIND).',
          { step: 0.25 },
        )}
        {top(
          'dischargeMarkMinHeal',
          'Discharge mark min heal (old)',
          'Old, kept one content version: min rest replaces it.',
          { integer: true },
        )}
        {top(
          'homeMarksPerDay',
          'Home marks per day',
          'The most marks home training gives per stat per day.',
          { integer: true },
        )}
      </Fieldset>
      <Fieldset legend="Headline">
        <TierSelectField
          label="Badly hurt tier"
          hint="WIND: at this tier or deeper the headline calls a survivor badly hurt."
          value={headline.badlyHurtTier}
          issues={errors['headline.badlyHurtTier']}
          onChange={(v) => set({ headline: { ...headline, badlyHurtTier: v } })}
        />
        {field(
          'headline.badlyHurtHealthFrac',
          'Badly hurt health share (old)',
          'Old, kept one content version: the badly hurt tier replaces it. [0, 1].',
          headline.badlyHurtHealthFrac,
          (n) => set({ headline: { ...headline, badlyHurtHealthFrac: n } }),
          share,
        )}
        {field(
          'headline.safeCapDays',
          'Safe cap days',
          '"Safe for N days" never shows more than this.',
          headline.safeCapDays,
          (n) => set({ headline: { ...headline, safeCapDays: n } }),
          { integer: true },
        )}
      </Fieldset>
      <Fieldset legend="Supplies">
        {field(
          'supplies.lowDays',
          'Low at days',
          'Food or water lasting this many days or fewer is low. Above the short days.',
          supplies.lowDays,
          (n) => set({ supplies: { ...supplies, lowDays: n } }),
          { integer: true },
        )}
        {field(
          'supplies.shortDays',
          'Short at days',
          'Lasting this many days or fewer is short (red).',
          supplies.shortDays,
          (n) => set({ supplies: { ...supplies, shortDays: n } }),
          { integer: true },
        )}
        {field(
          'supplies.minBandages',
          'Min bandages',
          'Fewer bandages than this flags medicine as low.',
          supplies.minBandages,
          (n) => set({ supplies: { ...supplies, minBandages: n } }),
          { integer: true },
        )}
        {field(
          'supplies.storageAlmostFullFrac',
          'Storage almost full share',
          'Storage this full shows the almost-full warning. (0, 1].',
          supplies.storageAlmostFullFrac,
          (n) => set({ supplies: { ...supplies, storageAlmostFullFrac: n } }),
          share,
        )}
        <ItemIdListEditor
          label="Bandage items"
          hint="Items counted as bandages. Empty = the bandage rule is off."
          value={supplies.bandageItemIds ?? []}
          errors={errors}
          base="supplies.bandageItemIds"
          onChange={(next) => set({ supplies: { ...supplies, bandageItemIds: next } })}
        />
      </Fieldset>
      <Fieldset legend="Sizes">
        {top('onTableMax', 'On the table max', 'The most items the On the table strip shows.', {
          integer: true,
          min: 1,
        })}
        {top('logMax', 'Log max', 'The most entries the home log keeps.', {
          integer: true,
          min: 1,
        })}
      </Fieldset>
    </>
  );
}

/** The item categories in the draft, as a hint for match lists. */
function useDraftCategories() {
  const b = useContentBundle();
  return [...new Set((b.items ?? []).map((i) => i?.category ?? '').filter((c) => c !== ''))].sort();
}

/**
 * One shelf of Inventory (`storageGroups[]`) or one tab of Build (`buildCategories[]`): shared-cs `ItemGroupDef`. An item goes to the
 * first group (by order) that matches it; `other` catches the rest. `reserved` names the ids the game keeps for itself.
 */
function ItemGroupFields({
  value,
  onChange,
  errors,
  reserved,
}: FormProps<ItemGroupDef> & { reserved: string }) {
  const g = withDefaults(itemGroupDefaults(), value ?? {});
  const m: ItemMatchDef = { ...itemGroupDefaults().match, ...g.match };
  const setMatch = (patch: Partial<ItemMatchDef>) => onChange({ ...g, match: { ...m, ...patch } });
  const categories = useDraftCategories();
  return (
    <>
      <Fieldset legend="Group">
        <TextField
          label="Id"
          mono
          hint={`Lower case, digits and _. Reserved: ${reserved}.`}
          value={g.id}
          issues={errors.id}
          onChange={(v) => onChange({ ...g, id: v })}
        />
        <TextField
          label="Name"
          value={g.name}
          issues={errors.name}
          onChange={(v) => onChange({ ...g, name: v })}
        />
        <NumberField
          label="Order"
          integer
          hint="Lower first; also the matching order."
          value={g.order}
          onChange={(v) => onChange({ ...g, order: v ?? 0 })}
        />
      </Fieldset>
      <Fieldset legend="Matches items that are…">
        <CheckboxField
          label="Survival (food or water)"
          checked={!!m.survival}
          onChange={(on) => setMatch({ survival: on })}
        />
        <CheckboxField
          label="Healing (heal above 0)"
          checked={!!m.healAbove0}
          onChange={(on) => setMatch({ healAbove0: on })}
        />
        <StringListEditor
          label="Categories"
          hint={categories.length ? `In the draft: ${categories.join(', ')}.` : undefined}
          value={m.categories ?? []}
          errors={errors}
          base="match.categories"
          onChange={(next) => setMatch({ categories: next })}
        />
        <StringListEditor
          label="Tags"
          value={m.tags ?? []}
          errors={errors}
          base="match.tags"
          onChange={(next) => setMatch({ tags: next })}
        />
        <ItemIdListEditor
          label="Items"
          value={m.itemIds ?? []}
          errors={errors}
          base="match.itemIds"
          onChange={(next) => setMatch({ itemIds: next })}
        />
        <div className="sm:col-span-2">
          <IssueList issues={errors.match} />
        </div>
      </Fieldset>
    </>
  );
}

export function StorageGroupForm(props: FormProps<ItemGroupDef>) {
  return <ItemGroupFields {...props} reserved="other" />;
}

export function BuildCategoryForm(props: FormProps<ItemGroupDef>) {
  return <ItemGroupFields {...props} reserved="next_level, base, other" />;
}

/**
 * `inventoryRules` (shared-cs `InventoryRulesDef`, ADR 0048): the limit toggles. An absent block means every limit is enforced (the
 * legacy behaviour), so a draft without it shows all three on; a present block's missing toggles are off.
 */
export function InventoryRulesForm({ value, onChange }: FormProps<InventoryRulesDef>) {
  const r = withDefaults(inventoryRulesBlockDefaults(), value ?? {});
  const set = (patch: Partial<InventoryRulesDef>) => onChange({ ...r, ...patch });
  return (
    <Fieldset legend="Limits">
      <CheckboxField
        label="Carry limits"
        hint="Overweight sprint and noise penalty, squad overflow spill, the key-part hard limit, vehicle cargo cap, auto-loot's stop."
        checked={!!r.carryLimitsEnabled}
        onChange={(on) => set({ carryLimitsEnabled: on })}
      />
      <CheckboxField
        label="Storage limits"
        hint="storage_full on stash, crafts and orders; the space orders hold; the arrival fill and overflow."
        checked={!!r.storageLimitsEnabled}
        onChange={(on) => set({ storageLimitsEnabled: on })}
      />
      <CheckboxField
        label="Loadout limits"
        hint="Per-slot and goods limits (loadout rules' max goods)."
        checked={!!r.loadoutLimitsEnabled}
        onChange={(on) => set({ loadoutLimitsEnabled: on })}
      />
      <CheckboxField
        label="Field kit uses the haul"
        hint="The phone's field kit crafts from raw materials in a pack's haul. Off retires the field kit."
        checked={!!r.fieldKitUsesHaul}
        onChange={(on) => set({ fieldKitUsesHaul: on })}
      />
      <CheckboxField
        label="Raid kit takes only held gear"
        hint="A raid checks out only the weapons and armor its members hold (plus what their empty slots auto-pick); spare weapons and armor stay in the pool for other teams. Off = every free good goes (ADR 0063)."
        checked={!!r.raidKitHeldOnly}
        onChange={(on) => set({ raidKitHeldOnly: on })}
      />
    </Fieldset>
  );
}

/** `loadoutRules` (shared-cs `LoadoutRulesDef`, ADR 0048 §7): the loadout's slots and the goods limit. No slots = the built-in four. */
export function LoadoutRulesForm({ value, onChange, errors }: FormProps<LoadoutRulesDef>) {
  const r = { ...loadoutRulesDefaults(), ...value };
  const slots = (r.slots ?? []).map((s) => s ?? { id: '', label: '', accepts: {} });
  const set = (patch: Partial<LoadoutRulesDef>) => onChange({ ...r, ...patch });
  return (
    <>
      <Fieldset legend="Goods">
        <NumberField
          label="Max goods"
          hint="The most goods (units, slots included) one loadout holds while loadout limits are on. 0 = no limit."
          integer
          min={0}
          value={r.maxGoods}
          issues={errors.maxGoods}
          onChange={(v) => set({ maxGoods: v ?? 0 })}
        />
      </Fieldset>
      <Fieldset legend="Slots">
        <ListEditor<LoadoutSlotDef>
          label="Slots"
          what="slot"
          hint="In display order. Empty = the built-in weapon1, weapon2, armor, backpack."
          items={slots}
          issues={errors.slots}
          onChange={(next) => set({ slots: next })}
          make={() => ({ id: '', label: '', accepts: { categories: [], tags: [] } })}
          row={(s, j, setS) => (
            <div className="grid gap-2 sm:grid-cols-2">
              <TextField
                label="Id"
                mono
                hint="lower camelCase."
                value={s.id}
                issues={errors[`slots.${j}.id`]}
                onChange={(v) => setS({ ...s, id: v })}
              />
              <TextField
                label="Label"
                value={s.label}
                issues={errors[`slots.${j}.label`]}
                onChange={(v) => setS({ ...s, label: v })}
              />
              <StringListEditor
                label="Accepts categories"
                value={s.accepts?.categories ?? []}
                errors={errors}
                base={`slots.${j}.accepts.categories`}
                onChange={(next) => setS({ ...s, accepts: { ...s.accepts, categories: next } })}
              />
              <StringListEditor
                label="Accepts tags"
                value={s.accepts?.tags ?? []}
                errors={errors}
                base={`slots.${j}.accepts.tags`}
                onChange={(next) => setS({ ...s, accepts: { ...s.accepts, tags: next } })}
              />
              <div className="sm:col-span-2">
                <IssueList issues={issuesUnder(errors, `slots.${j}.accepts`)} />
                <IssueList issues={errors[`slots.${j}`]} />
              </div>
            </div>
          )}
        />
        {slots.length === 0 && (
          <button
            type="button"
            className="rounded border-2 border-ink px-2 py-0.5 text-xs font-semibold"
            onClick={() => set({ slots: defaultLoadoutSlots() })}
          >
            Start from the built-in slots
          </button>
        )}
      </Fieldset>
    </>
  );
}
